import { TenantESignConfig } from '@celito.clients/endpoints';
import { ESignAuthContext, ESignResult } from '@celito.clients/provider';
import type { WidgetOptions } from '@okta/okta-signin-widget';
import { lazy } from 'react';

import { buildOktaWidgetConfig } from '../utils/config';
import {
  ESignWidgetRenderProps,
  WidgetESignProvider,
} from './esign-provider.model';

// Lazy so the heavy Okta bundle is only pulled when a tenant actually signs
// with Okta. The modal keeps the <Suspense> boundary around renderWidget().
const OktaSignInWidget = lazy(() => import('../utils/okta-signin-widget'));

/**
 * WI-F7 — Okta e-sign provider (ADO #38535).
 *
 * Wraps the existing in-modal Okta Sign-In Widget flow. Config (issuer /
 * clientId / origin-derived redirect) comes from the runtime
 * `TenantConfig.esign` rather than build-time `VITE_OKTA_*` env. The widget's
 * Safari revoke/close-session workaround lives in `okta-signin-widget.tsx` and
 * is untouched.
 */

/**
 * Extract the RAW JWT string from an Okta widget token response.
 *
 * The ID TOKEN is the one to send, and the order below is the whole point of
 * this function. `showSignInToGetTokens` returns token OBJECTS
 * (`{ idToken: { idToken: '<jwt>' }, accessToken: { accessToken: '<jwt>' } }`)
 * and the widget's default scopes yield BOTH, so whichever branch runs first
 * decides what the backend receives.
 *
 * The backend rejects the access token twice over. `verifyEsignToken` pins
 * `audience` to the tenant's `esignClientId` — only the ID token carries the
 * client id as its `aud`, so an access token fails the jose check and surfaces
 * as the opaque "signature, issuer, audience or expiry check failed". Past
 * that, `assertIsIdToken` refuses anything holding `scp`/`roles` precisely
 * because it is "an ACCESS token, not the ID token minted by the
 * re-authentication ceremony".
 *
 * The access-token branch stays only as a fallback for a response that carries
 * no ID token at all. Do not reorder these.
 */
export const extractOktaRawToken = (response: unknown): string => {
  const resp = response as
    | { accessToken?: unknown; idToken?: unknown }
    | null
    | undefined;

  const idToken = resp?.idToken as string | { idToken?: string } | undefined;
  if (typeof idToken === 'string') return idToken;
  if (idToken?.idToken) return idToken.idToken;

  const accessToken = resp?.accessToken as
    | string
    | { accessToken?: string }
    | undefined;
  if (typeof accessToken === 'string') return accessToken;
  if (accessToken?.accessToken) return accessToken.accessToken;

  throw new Error('Okta sign-in returned no token.');
};

export class OktaWidgetProvider implements WidgetESignProvider {
  public readonly provider = 'okta';

  constructor(
    private readonly esign: Pick<TenantESignConfig, 'issuer' | 'clientId'>
  ) {}

  /** The Okta widget options built from the tenant's runtime e-sign config. */
  public getWidgetConfig(): WidgetOptions {
    return buildOktaWidgetConfig(this.esign);
  }

  /** Map a raw Okta widget response to an {@link ESignResult}. */
  public extractResult(response: unknown): ESignResult {
    return {
      rawToken: extractOktaRawToken(response),
      provider: this.provider,
    };
  }

  /**
   * Okta authenticates through the in-modal widget (see {@link renderWidget}),
   * not a headless promise. `authenticate` exists to satisfy the
   * `ESignProvider` contract; the modal drives Okta via `renderWidget`.
   */
  public authenticate(_ctx: ESignAuthContext): Promise<ESignResult> {
    return Promise.reject(
      new Error(
        'OktaWidgetProvider authenticates via renderWidget(), not authenticate().'
      )
    );
  }

  public renderWidget({
    onSuccess,
    onError,
    recordName,
    objectName,
  }: ESignWidgetRenderProps) {
    return (
      <OktaSignInWidget
        config={this.getWidgetConfig()}
        onSuccess={(response: unknown) => {
          try {
            onSuccess(this.extractResult(response));
          } catch (err) {
            onError(err as Error);
          }
        }}
        onError={onError}
        recordName={recordName ?? ''}
        objectName={objectName ?? ''}
      />
    );
  }
}
